import { NoData } from '@/components/ui/NoData';
import { QueryFolders } from '@/sections/insights/queryBuilder/savedQueries/folders';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';

import { QueryBuilderHeader } from './QueryBuilderHeader';
import { QueryBuilderHeaderButtons } from './QueryBuilderHeaderButtons';
import { QueryEditor, QueryViewer } from './QueryDisplay';
import { QueryResults } from './QueryResults';
import {
  QuerySplitEditor,
  QuerySplitViewer,
  QuerySplitWriter,
} from './QuerySplitDisplay';

/**
 * QueryBuilderWorkstation - Main content area for query building and results
 *
 * Displays different components based on workstation mode:
 * - No query selected: Landing state with "Select a query" message
 * - mode === 'query': QueryResults for query execution and results
 * - mode === 'edit': QueryEditor for creating/editing queries
 * - mode === 'view': QueryViewer for read-only query inspection
 *
 * Mode is controlled by:
 * - QueryBuilderSidebar: Clicking query sets mode to 'query'
 * - QueryMenu: View/Edit/Duplicate actions set mode accordingly
 *
 * Header is rendered once here with editable prop based on mode
 */
export const QueryBuilderWorkstation = () => {
  const { mode, queryState, getSavedQuery } = useQueryBuilder();

  if (mode === null || queryState === null) {
    return (
      <div className="flex h-full w-full flex-col overflow-hidden p-6">
        <div className="mb-4 flex items-start gap-4">
          <div className="flex-1" />
          <QueryBuilderHeaderButtons />
        </div>
        <div
          className="flex flex-1 flex-col"
          data-testid="query-builder-landing-state"
        >
          <NoData
            title="Query Builder"
            description="Select an existing query to the right to get started"
          />
        </div>
      </div>
    );
  }

  const isEditableMode = ['edit', 'split-edit', 'split-writer'].includes(mode);
  const isEditableQuery =
    queryState.metadata.folderId === QueryFolders.CUSTOM_QUERIES;

  const isEditable = isEditableMode && isEditableQuery;

  return (
    <div className="flex h-full w-full flex-col p-6 pl-8">
      <div className="mb-4 flex items-start gap-4">
        <QueryBuilderHeader editable={isEditable} />
        <QueryBuilderHeaderButtons />
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        {mode === 'query' && <QueryResults selectedQuery={getSavedQuery()} />}
        {mode === 'edit' && <QueryEditor />}
        {mode === 'view' && <QueryViewer />}
        {mode === 'split-edit' && <QuerySplitEditor />}
        {mode === 'split-view' && <QuerySplitViewer />}
        {mode === 'split-writer' && <QuerySplitWriter />}
      </div>
    </div>
  );
};
